import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, format } from 'date-fns';

import { CollapseIn, ScrollFade } from '@app/components/core';
import { WorkdayDateField } from '@app/components/shared/reportModal/WorkdayDateField';
import { calendarDay, shortDate } from '@app/utils/helpers/date-utils';
import { useOrganizationId } from '@app/utils/hooks';
import {
  ProductStatus,
  ReportEntity,
  useCreateIncident,
  useDailyReportDefaults,
  useListProductManagers,
  useListProducts,
  useListRoles,
  useProductIncidents,
  useUpdateIncident,
} from '@data';
import { useListPmMembers } from '@data/hooks/membership';
import { useStore } from '@data/storages';
import { Roles, normalizeRole } from '@app/utils/types';
import { Button } from '@ui/components/ui/button';
import { Checkbox } from '@ui/components/ui/checkbox';
import { DialogFooter } from '@ui/components/ui/dialog';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField } from '@ui/components/ui/multi-select';
import { Textarea } from '@ui/components/ui/textarea';
import { TimeField } from '@ui/components/ui/time-field';
import { cn } from '@ui/lib/utils';

import { incidentOption } from '../../pages/organizations/organization/clientReporting/product/sources/incidents';
import { peopleForProduct } from './incidentPeople';
import NewIncidentFields from './NewIncidentFields';
import {
  emptyNewIncident,
  NEW_INCIDENT,
  isNewIncidentAnswered,
  newIncidentAnsweredAt,
  newIncidentError,
  newIncidentReportedAt,
  newIncidentValues,
  type NewIncidentDraft,
} from './newIncident';
import {
  clockTimeOf,
  hoursFromMinutes,
  instantAfterStart,
  instantOn,
  resolvedErrorFor,
  lengthLabel,
  minutesWorked,
  runsPastMidnight,
  startsBeforeIncident,
  timeErrorFor,
} from './incidentLength';

/** Everyone in the organization, unfiltered. Held outside the component: it is part of a query key. */
const EVERY_MEMBER = {};

const toDateOrNull = (at?: string | null): Date | null => (at ? new Date(at) : null);

/** Reporting the time you spent fixing an incident, from the Reporting page. */
const ReportIncidentForm = ({
  show,
  onHide,
  userId,
  report,
  against,
  prefill,
  onSubmit,
  bodyClassName,
  fill,
  hideWorkday,
  hideDescription,
  showAhead,
  onLoggingNewChange,
}: {
  show: boolean;
  onHide: () => void;
  userId?: string;
  /** The entry being changed, or nothing when reporting new work. */
  report?: ReportEntity | null;
  /** The incident to book against, when the form is opened from that incident's own page. */
  against?: { productId: string; incidentId: string } | null;
  /** What the form that handed over to this one had already filled in. */
  prefill?: { productId?: string; workday?: Date; today?: string } | null;
  /** Writes the time entry. The same call the day's own form makes, so this is one ledger, not two. */
  onSubmit: (values: Partial<ReportEntity>) => Promise<unknown>;
  /** The height this and the panel beside it start at, so moving between them does not resize the window. */
  bodyClassName?: string;
  /** Takes the height its holder gives it, scrolling inside, instead of capping itself. */
  fill?: boolean;
  /** Leaves the day off the form, for a holder that already fixed it through `prefill`. */
  hideWorkday?: boolean;
  /** Leaves off the line saying what the form is for, for a holder whose tab already says it. */
  hideDescription?: boolean;
  /** Shows the later questions from the start, greyed out until a product is chosen, instead of sliding them in. */
  showAhead?: boolean;
  /** Whether the form is logging an incident nobody has logged yet, the long version of it. */
  onLoggingNewChange?: (isLoggingNew: boolean) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();

  const [productId, setProductId] = useState('');
  const [incidentId, setIncidentId] = useState('');
  // Whose hours these are. Yours unless somebody says otherwise, which is what the picker is for.
  const [personId, setPersonId] = useState('');
  /** The incident being logged, when there is one. The same answer the day's report asks for. */
  const [newIncident, setNewIncident] = useState<NewIncidentDraft>(emptyNewIncident());
  const [workday, setWorkday] = useState<Date>(calendarDay(new Date()));
  const [calendarOpen, setCalendarOpen] = useState(false);
  /** The two ends of the work, as clock times on the day above. The hours fall out of the pair. */
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  /** What is in the boxes mid-typing, which is what a half-answered time has to be judged on. */
  const [startTyped, setStartTyped] = useState<{ partial: boolean; latest?: string }>({ partial: false });
  const [endTyped, setEndTyped] = useState<{ partial: boolean; latest?: string }>({ partial: false });
  const [today, setToday] = useState('');
  const [saving, setSaving] = useState(false);
  /** A refused write, and whether the incident was resolved on the way to it. */
  const [failed, setFailed] = useState<'afterResolving' | 'nothing' | null>(null);
  /** Whether this stretch of work is what fixed it, which resolves the incident at the moment it ended. */
  const [resolvedIt, setResolvedIt] = useState(false);
  /** A refused resolution, and whether an incident was logged on the way to it. */
  const [resolveFailed, setResolveFailed] = useState<'logged' | 'nothing' | null>(null);
  // A refused attempt's incident, so a retry cites it rather than logging the same outage twice.
  const logged = useRef<{ writing: string; id: string } | null>(null);

  const { data: productData } = useListProducts(organizationId, {
    statuses: [ProductStatus.active, ProductStatus.support],
  });

  // The server refuses a userId that is not the caller's unless they are an admin or a PM.
  const { organization, tokenData } = useStore();
  const viewerRole = normalizeRole(organization?.role ?? '');
  const canPickPerson = !report && !!against && (viewerRole === Roles.admin || viewerRole === Roles.productManager);

  const { data: memberData } = useListPmMembers(organizationId, EVERY_MEMBER, canPickPerson && show);
  // The product's own team, asked for per product because that is what orders the list.
  const { data: teamData } = useListProductManagers(canPickPerson && productId ? productId : undefined);
  const { data: defaults, isFetching: defaultsLoading } = useDailyReportDefaults(organizationId, show);
  const { data: incidentData } = useProductIncidents(productId || undefined);
  const createIncident = useCreateIncident();
  const updateIncident = useUpdateIncident();

  // Every product: one without an agreement simply has no targets to judge an incident by.
  const products = useMemo(() => productData?.items ?? [], [productData?.items]);

  // Everything the organization is not done with.
  const incidents = useMemo(
    () => (incidentData?.items ?? []).filter(incident => !incident.closedAt || incident.id === report?.incidentId),
    [incidentData?.items, report?.incidentId],
  );

  const people = useMemo(
    () => peopleForProduct(memberData?.items ?? [], teamData?.items ?? [], tokenData?.userId),
    [memberData?.items, teamData?.items, tokenData?.userId],
  );

  const isEditing = !!report;

  // Before the paint: an ordinary effect painted the form empty and then unrolled it to its real size.
  useLayoutEffect(() => {
    if (!show) return;
    const now = new Date();
    // The day being reported is when the incident most likely came in; the hour is left blank on purpose.
    setNewIncident(emptyNewIncident(calendarDay(prefill?.workday ?? now)));
    // Closing an incident is never what reopening the form is for, so it is always asked for again.
    setFailed(null);
    setResolveFailed(null);
    setResolvedIt(false);
    logged.current = null;

    if (report) {
      // The product falls back to the caller's: a sprint's own reports carry the person, not the product.
      setProductId(report.involvement?.product?.id ?? against?.productId ?? '');
      setIncidentId(report.incidentId ?? '');
      setPersonId(report.involvement?.membership?.user?.id ?? userId ?? '');
      setWorkday(calendarDay(report.workday));
      // An older entry has no start to put back, so the pair is answered again from what it says.
      const began = report.startedAt ? new Date(report.startedAt) : null;
      setStartTime(began ? clockTimeOf(began) : '');
      setEndTime(began ? clockTimeOf(new Date(began.getTime() + report.minutes * 60000)) : '');
      setToday(report.today ?? '');
      return;
    }

    setProductId(against?.productId ?? prefill?.productId ?? '');
    setIncidentId(against?.incidentId ?? '');
    // Your own day is the answer nearly every time, so it is the one the form opens on.
    setPersonId(userId ?? '');
    setWorkday(calendarDay(prefill?.workday ?? now));
    setStartTime('');
    setEndTime('');
    setToday(prefill?.today ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, report, userId, against?.productId, against?.incidentId, prefill?.productId, prefill?.workday]);

  const isNew = incidentId === NEW_INCIDENT;
  useEffect(() => onLoggingNewChange?.(isNew), [isNew, onLoggingNewChange]);

  // The day and the two clock times are the answer; the hours on the entry are what falls out of them.
  const minutes = minutesWorked(startTime, endTime);
  const numericHours = hoursFromMinutes(minutes);
  const endsNextDay = runsPastMidnight(startTime, endTime);

  // Only for one being logged here: an existing incident's answer is changed on its own page.
  const answeredError = isNew ? newIncidentError(newIncident) : undefined;

  // When it came in: off the record if already logged, off the fields above if being logged here.
  const chosen = incidents.find(incident => incident.id === incidentId);
  const reportedAt = isNew ? newIncidentReportedAt(newIncident) : ((chosen && new Date(chosen.reportedAt)) ?? null);

  // An incident already resolved has nothing left to resolve, and reopening one is done on its own page.
  const canResolve = isNew || (!!chosen && !chosen.resolvedAt);
  const resolvesAt = startTime && endTime ? instantAfterStart(workday, startTime, endTime) : null;
  /** An acknowledgement after the work stopped: the server refuses that resolution, so it is said here first. */
  const acknowledgedAt = isNew ? newIncidentAnsweredAt(newIncident) : toDateOrNull(chosen?.respondedAt);
  const resolveOrderError = !!(resolvedIt && canResolve && resolvesAt && acknowledgedAt && resolvesAt < acknowledgedAt);
  // The server refuses a fix that has not happened yet.
  const resolveInFuture = !!(resolvedIt && canResolve && resolvedErrorFor(resolvesAt));
  /** The server refuses a fix a pause still runs past, so the pause is named here rather than at save time. */
  const pausePastFix =
    resolvedIt && canResolve && resolvesAt
      ? (chosen?.pauses ?? []).find(pause => !pause.endedAt || new Date(pause.endedAt) > resolvesAt)
      : undefined;

  // Judged on the latest time the start could still reach: once even that is too early, no keystroke saves it.
  const orderError = startsBeforeIncident(startTyped.latest ?? startTime, workday, reportedAt)
    ? 'reportIncident.beforeIncident'
    : undefined;
  // Both ends, and both finished: 09:0 on its way to 09:05 would read as no time worked.
  const lengthError =
    startTime && endTime && !startTyped.partial && !endTyped.partial ? timeErrorFor(minutes) : undefined;
  const timeError = orderError ?? lengthError;

  // Only the day arrives filled in, and only because today is the overwhelmingly likely one.
  const isForSomebodyElse = !!personId && !!userId && personId !== userId;

  /** Whose chapter the hours are filed under, asked per person rather than taken from the caller's. */
  // Empty ids rather than undefined: that is what the hook reads as "nothing to ask yet".
  const { data: personRoles, isFetching: rolesLoading } = useListRoles({
    userId: personId || userId || '',
    productId: productId ?? '',
  });
  const chapterForPerson = useMemo(() => {
    // `chosenChapter`, not `chapter`: the latter hides two guesses under the one they actually chose.
    const chosen = isForSomebodyElse
      ? people.find(person => person.userId === personId)?.chapter
      : defaults?.chosenChapter;

    return chosen ?? personRoles?.items.find(role => role.isMostRecentInvolvement)?.role;
  }, [isForSomebodyElse, people, personId, personRoles, defaults]);

  const canSave =
    !!productId &&
    !!incidentId &&
    !!(personId || userId) &&
    !!startTime &&
    !!endTime &&
    !timeError &&
    !!today.trim() &&
    !!chapterForPerson &&
    numericHours > 0 &&
    !answeredError &&
    !resolveOrderError &&
    !resolveInFuture &&
    !pausePastFix &&
    (!isNew || isNewIncidentAnswered(newIncident));

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setFailed(null);
    setResolveFailed(null);
    try {
      // Keyed on what was written, so a retry cites the existing incident and a corrected answer logs a new one.
      const writing = isNew ? JSON.stringify({ productId, values: newIncidentValues(newIncident) }) : '';
      if (isNew && logged.current?.writing !== writing) {
        const incident = await createIncident.mutateAsync({ productId, values: newIncidentValues(newIncident) });
        logged.current = { writing, id: incident.id };
      }
    } catch {
      // The write did not land, so nothing was recorded. Staying open keeps what was typed.
      setFailed('nothing');
      setSaving(false);
      return;
    }

    let resolved = false;

    // Before the hours: a refusal after `onSubmit` renders nowhere, and a retry repeats the same moment.
    if (resolvedIt && canResolve && resolvesAt) {
      try {
        await updateIncident.mutateAsync({
          productId,
          incidentId: isNew ? logged.current!.id : incidentId,
          values: { resolvedAt: resolvesAt.toISOString() },
        });
        resolved = true;
      } catch {
        // The hours are not booked either way; an incident logged a moment ago is the one thing that is.
        setResolveFailed(isNew ? 'logged' : 'nothing');
        setSaving(false);
        return;
      }
    }

    try {
      await onSubmit({
        // Editing writes back to the entry that was opened; the id is what says so.
        ...(report ? { id: report.id } : {}),
        userId: personId || userId,
        productId,
        role: report?.role ?? chapterForPerson,
        workday,
        today: today.trim(),
        hours: numericHours,
        // The hour the work began, which is what decides whether it fell outside the service window.
        startedAt: instantOn(workday, startTime).toISOString(),
        incidentId: isNew ? logged.current!.id : incidentId,
      } as Partial<ReportEntity>);
    } catch {
      setFailed(resolved ? 'afterResolving' : 'nothing');
      setSaving(false);
      return;
    }

    setSaving(false);
    onHide();
  };

  const productOptions = products.map(product => ({ value: product.id, label: product.name }));
  const incidentOptions = [
    ...incidents.map(incident => ({
      value: incident.id,
      ...incidentOption(incident, t),
    })),
    { value: NEW_INCIDENT, label: t('reportIncident.notLoggedYet') },
  ];
  // Only once a product is chosen and its team has arrived: before that, a pill on every row is a lie.
  const isTeamKnown = !!productId && !!teamData;
  const personOptions = people.map(person => ({
    value: person.userId,
    label: person.name,
    tags: [
      ...(person.isYou ? [{ text: t('reportIncident.you'), tone: 'neutral' as const }] : []),
      ...(isTeamKnown && !person.onTeam ? [{ text: t('reportIncident.notOnProduct'), tone: 'neutral' as const }] : []),
    ],
  }));

  const resolvedCheckbox = (
    <div className="flex items-center gap-2">
      <Checkbox
        id="report-incident-resolved"
        // base-ui's Checkbox is a span, so a disabled fieldset around it does not reach it.
        disabled={!incidentId}
        checked={resolvedIt}
        onCheckedChange={next => setResolvedIt(next === true)}
      />
      <label
        id="report-incident-resolved-label"
        htmlFor="report-incident-resolved"
        className="cursor-pointer text-sm text-foreground"
      >
        {t('reportIncident.incidentResolved')}
      </label>
    </div>
  );

  const whichIncident = (
    <MultiSelectField
      single
      clearable={false}
      id="report-incident-which"
      label={t('reportIncident.which')}
      options={incidentOptions}
      value={incidentId ? [incidentId] : []}
      onChange={next => {
        setIncidentId(next[0] ?? '');
        setResolvedIt(false);
      }}
      emptyLabel={t('reportIncident.pickIncident')}
      // Short list, and every row is a sentence: a search box over four incidents is furniture.
      searchable={false}
    />
  );

  return (
    <>
      <div className={cn(bodyClassName, fill && 'flex min-h-0 flex-1 flex-col')}>
        {/* Said by the form rather than by whatever is holding it: this is a panel in the entry form as
          often as it is a dialog of its own, and either way it should say what it is for. */}
        {hideDescription ? null : (
          <p className="mb-4 text-sm text-muted-foreground">
            {isEditing ? t('reportIncident.editDescription') : t('reportIncident.description')}
          </p>
        )}

        {/* The product and the day on one row, and outside the scrolling box below, where they have to
            stay: `overflow` clips absolutely-positioned descendants however high their z-index, and the
            day's calendar is one. The same arrangement as the daily report, for the same reason. */}
        <div className="mb-4 flex flex-wrap items-start gap-3">
          <MultiSelectField
            single
            clearable={false}
            className="min-w-[12rem] flex-1"
            id="report-incident-product"
            label={t('titles.product')}
            options={productOptions}
            value={productId ? [productId] : []}
            onChange={next => {
              setProductId(next[0] ?? '');
              setIncidentId('');
              setResolvedIt(false);
            }}
            emptyLabel={t('reportIncident.pickProduct')}
            searchPlaceholder={t('searchProduct')}
            emptySearchLabel={t('noProductsFound')}
          />
          {showAhead ? (
            <fieldset
              disabled={!productId}
              className="min-w-[12rem] flex-1 border-0 p-0 transition-opacity disabled:opacity-50"
            >
              {whichIncident}
            </fieldset>
          ) : null}
          {hideWorkday ? null : (
            <WorkdayDateField
              className="w-64 shrink-0"
              value={workday}
              onChange={setWorkday}
              open={calendarOpen}
              onOpenChange={setCalendarOpen}
              label={t('titles.workday')}
              placeholder={t('phillReport.pickDay')}
              // Nobody has fixed tomorrow's incident yet, and a mistyped year is the usual way one arrives.
              disabledDays={{ after: new Date() }}
            />
          )}
        </div>

        {/* No `gap`: the answers below stay mounted while collapsed, and a gap utility spaces them at
            zero height too, leaving a blank line per question nobody has reached yet. Each block carries
            its own bottom padding instead.

            This scrolls, and the product and the day above it deliberately do not: their calendar is the
            one panel here that has nowhere to go inside a box that clips. What it does clip is the focus
            ring at the two edges and, when it is open, the foot of the reported-at calendar — which
            scrolls itself into view. The horizontal padding is there for the rings on the sides; the
            fade says there is more below, since a form cut off mid-question reads as the end of it. */}
        <ScrollFade
          className={cn('-mx-1 px-1 pb-1', fill ? 'h-full' : 'max-h-[calc(70vh-14rem)]')}
          wrapperClassName={fill ? 'min-h-0 flex-1' : undefined}
          fadeClassName="from-popover"
        >
          <div className="flex flex-col">
            {/* Whose hours these are, above the incident rather than below it: it is the first thing that
                is not already answered by having opened this form, and an entry filed against the wrong
                person is the one mistake here that nobody notices until a month is being invoiced. */}
            {canPickPerson && (
              <div className="pb-4">
                <MultiSelectField
                  single
                  clearable={false}
                  id="report-incident-person"
                  label={t('reportIncident.person')}
                  options={personOptions}
                  value={personId ? [personId] : []}
                  onChange={next => setPersonId(next[0] ?? '')}
                  emptyLabel={t('reportIncident.pickPerson')}
                  searchPlaceholder={t('search')}
                  emptySearchLabel={t('noMatches')}
                />
              </div>
            )}

            {/* Each answer opens the next one, the way the day's own report opens an hours field when a
              product is ticked. Sliding rather than appearing: the question that just arrived is the one
              under the cursor, and a form that jumps makes people look for what moved. */}
            {showAhead ? null : (
              <CollapseIn show={!!productId}>
                <div className="pb-4">{whichIncident}</div>
              </CollapseIn>
            )}

            <CollapseIn show={isNew}>
              <div className="mb-4">
                <NewIncidentFields
                  idPrefix="report-incident"
                  value={newIncident}
                  onChange={setNewIncident}
                  // The one door with room for the write-up, rather than answering for a day.
                  withDescription
                />
              </div>
            </CollapseIn>

            <CollapseIn show={!!incidentId || !!showAhead}>
              <fieldset disabled={!incidentId} className="min-w-0 border-0 p-0 transition-opacity disabled:opacity-50">
                {/* Only the clock, on the day above. Two of them side by side, because "from eleven to half
                one" is one answer and reads as one. */}
                <div className="flex flex-col gap-1.5 pb-4">
                  {/* Top-aligned, so a field that grows a message of its own drops it below itself rather
                  than pushing the other one out of line with it. Two clocks read as a pair, and a pair
                  that steps down mid-answer reads as two separate questions. */}
                  <div className="flex flex-wrap items-start gap-3">
                    {/* The two ends of the clock, one on each field: between them the pair says the hours
                    run 00 to 23 without a word of explanation, and neither ghost is a plausible answer
                    somebody would leave sitting there by accident. */}
                    <TimeField
                      required
                      className="min-w-[8rem] flex-1"
                      id="report-incident-started"
                      label={t('reportIncident.startedAt')}
                      value={startTime}
                      onChange={setStartTime}
                      onTyping={setStartTyped}
                      invalidMessage={t('reportIncident.badTime')}
                      hourPlaceholder="00"
                      minutePlaceholder="00"
                    />
                    {/* The day the end time falls on, shown beside it only once it has moved. The pair is
                    read as one clock reading, so the day it silently rolled over to belongs against the
                    boxes rather than in a sentence underneath, where it is a fact to be gone back for.
                    On the right, where it appears and disappears without shifting the boxes people are
                    typing in. */}
                    <div className="flex min-w-[8rem] flex-1 items-start gap-2">
                      <TimeField
                        required
                        id="report-incident-ended"
                        label={t('reportIncident.endedAt')}
                        value={endTime}
                        onChange={setEndTime}
                        onTyping={setEndTyped}
                        invalidMessage={t('reportIncident.badTime')}
                        hourPlaceholder="23"
                        minutePlaceholder="59"
                      />
                      {endsNextDay && (
                        // Mirrors the field's shape: a label's space, then a box's height to sit in.
                        <div className="flex flex-col gap-1.5">
                          <Label aria-hidden className="invisible">
                            {t('reportIncident.endedAt')}
                          </Label>
                          <span className="flex h-8 items-center text-xs tabular-nums text-muted-foreground">
                            ({format(addDays(workday, 1), 'dd/MM/yyyy')})
                          </span>
                        </div>
                      )}
                    </div>
                    {showAhead && (canResolve || !incidentId) ? (
                      // A label's space, then a box's height, so it lines up with the two clocks.
                      <div className="flex flex-col gap-1.5">
                        <Label aria-hidden className="invisible">
                          {t('reportIncident.endedAt')}
                        </Label>
                        <div className="flex h-8 items-center">{resolvedCheckbox}</div>
                      </div>
                    ) : null}
                  </div>
                  {timeError ? (
                    <p className="text-sm text-destructive">{t(timeError)}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {minutes > 0
                        ? `${t('reportIncident.lasted', { length: lengthLabel(minutes), hours: numericHours })}${
                            // Only that it wrapped; which day is already beside the field it happened to.
                            endsNextDay ? ` ${t('reportIncident.pastMidnight')}` : ''
                          }`
                        : t('phillReport.startedAtHint')}
                    </p>
                  )}
                </div>

                {canResolve && (
                  <div className="flex flex-col gap-1.5 pb-4 empty:hidden">
                    {showAhead ? null : resolvedCheckbox}
                    {resolveInFuture && (
                      <p className="text-sm text-destructive">{t('reportIncident.resolvedInFuture')}</p>
                    )}
                    {pausePastFix && <p className="text-sm text-destructive">{t('reportIncident.pauseRunsPastFix')}</p>}
                    {resolveOrderError && acknowledgedAt && (
                      <p className="text-sm text-destructive">
                        {t('reportIncident.resolvedBeforeAcknowledged', {
                          at: format(acknowledgedAt, 'dd/MM/yyyy HH:mm'),
                        })}
                      </p>
                    )}
                    {resolvedIt &&
                      resolvesAt &&
                      !timeError &&
                      !resolveOrderError &&
                      !pausePastFix &&
                      !resolveInFuture && (
                        <p className="text-xs text-muted-foreground">
                          {t('reportIncident.resolvedAtTime', { at: format(resolvesAt, 'dd/MM/yyyy HH:mm') })}
                        </p>
                      )}
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="report-incident-today">{t('titles.today', { date: shortDate(workday) })}</Label>
                  <Textarea
                    id="report-incident-today"
                    value={today}
                    rows={3}
                    className={showAhead ? 'min-h-[106px]' : undefined}
                    onChange={event => setToday(event.target.value)}
                  />
                </div>
              </fieldset>
            </CollapseIn>

            {/* Said rather than left as a dead button, once a product is chosen and both tiers have answered. */}
            {!!productId && !!(personId || userId) && !chapterForPerson && !rolesLoading && !defaultsLoading && (
              <p className="text-sm text-destructive">{t('reportIncident.noChapterForPerson')}</p>
            )}
            {failed && (
              <p className="text-sm text-destructive">
                {failed === 'afterResolving'
                  ? t(isEditing ? 'reportIncident.editFailedAfterResolving' : 'reportIncident.failedAfterResolving')
                  : t(isEditing ? 'reportIncident.editFailed' : 'reportIncident.failed')}
              </p>
            )}
            {resolveFailed && (
              <p className="text-sm text-destructive">
                {t(
                  resolveFailed === 'logged'
                    ? 'reportIncident.resolveFailedAfterLogging'
                    : 'reportIncident.resolveFailed',
                )}
              </p>
            )}
          </div>
        </ScrollFade>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onHide} disabled={saving}>
          {t('actions.cancel')}
        </Button>
        <Button onClick={save} disabled={!canSave || saving} loading={saving}>
          {isEditing ? t('reportIncident.saveEdit') : t('reportIncident.submit')}
        </Button>
      </DialogFooter>
    </>
  );
};

export default ReportIncidentForm;
